import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar, Search, Eye, Download, Truck, CheckCircle, Gift, Sparkles, MapPin, HeartHandshake } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import AdminPagination from '../../components/admin/AdminPagination';
import { fetchAllAdminPages } from '../../utils/fetchAllAdminPages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setView] = useState('bookings');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const requestId = React.useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setDebouncedSearch(searchQuery.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchBookings = React.useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    try {
      const params = { page, search: debouncedSearch };
      if (view === 'bookings' && statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get(`${API}/bookings/admin/${view === 'drafts' ? 'drafts' : 'all'}`, {
        params,
        withCredentials: true
      });
      if (currentRequest !== requestId.current) return;
      setBookings(response.data.items);
      setTotal(response.data.total);
      setPages(response.data.pages);
      if (page > Math.max(1, response.data.pages)) setPage(Math.max(1, response.data.pages));
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [statusFilter, view, page, debouncedSearch]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings();
      if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateTrackingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/tracking`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Tracking status updated to ${newStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update tracking');
    }
  };

  const updateVideoUrl = async (bookingId, videoUrl) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/video`,
        { video_url: videoUrl },
        { withCredentials: true }
      );
      toast.success('Video URL updated successfully');
      fetchBookings();
      if (selectedBooking && selectedBooking.id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, video_url: videoUrl }));
      }
    } catch (error) {
      toast.error('Failed to update video URL');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    processing: 'bg-indigo-100 text-indigo-700',
    puja_scheduled: 'bg-amber-100 text-amber-700',
    puja_completed: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-emerald-100 text-emerald-700',
    payment_failed: 'bg-rose-100 text-rose-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const filteredBookings = bookings;

  const exportBookings = async () => {
    try {
      const params = { search: debouncedSearch };
      if (view === 'bookings' && statusFilter !== 'all') params.status = statusFilter;
      const allBookings = await fetchAllAdminPages(`${API}/bookings/admin/${view === 'drafts' ? 'drafts' : 'all'}`, params);
      downloadCSV(allBookings);
    } catch (error) {
      toast.error('Failed to export bookings');
    }
  };

  // CSV Export
  const downloadCSV = (dataToExport) => {
    if (!dataToExport.length) {
      toast.error('No bookings to export');
      return;
    }

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const headers = [
      'Booking ID',
      'Date & Time',
      'Customer Name',
      'Customer Email',
      'Temple Name',
      'Puja Name',
      'Package Name',
      'Puja Date',
      'Devotee Names',
      'Gotras',
      'Dakshina (Rs)',
      'Prasad Preference',
      'Prasad Delivery Address',
      'Puja Wish',
      'Amount (Rs)',
      'Booking Status',
      'Video URL'
    ];

    const rows = dataToExport.map(b => [
      b.id,
      b.created_at ? new Date(b.created_at).toLocaleString('en-IN') : '',
      b.user?.name || b.contact?.name || b.delivery_address?.name || '',
      b.user?.email || b.contact?.email || '',
      b.temple?.name || '',
      b.puja?.name || '',
      b.package_name || '',
      b.puja_date || '',
      (b.devotee_names || []).join('; '),
      (b.gotras || []).join('; '),
      b.dakshina || 0,
      b.has_prasad ? 'Yes' : 'No',
      b.has_prasad ? `${b.delivery_address?.address || ''}, ${b.delivery_address?.city || ''} ${b.delivery_address?.pincode || ''}` : 'N/A',
      b.puja_wish || b.special_instructions || '',
      b.amount,
      b.status,
      b.video_url || ''
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `puja_bookings_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Bookings exported successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1A202C]">Puja Bookings</h2>
          <p className="text-gray-500 text-sm">Manage sacred temple rituals, devotees, sankalps, and prasad tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={exportBookings}
            variant="outline"
            className="flex items-center gap-2 text-xs"
            disabled={!total}
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export all CSV</span>
          </Button>
          <div className="bg-[#6E1B34] text-white text-xs font-bold px-3 py-1.5 rounded-xl">
            {total} {view === 'drafts' ? 'Drafts' : 'Bookings'}
          </div>
        </div>
      </div>

      {/* Search & Status Filters */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-2xs space-y-3">
        <div className="flex gap-2" role="tablist" aria-label="Booking type">
          {[{ id: 'bookings', label: 'Bookings' }, { id: 'drafts', label: 'Draft bookings' }].map(option => (
            <button key={option.id} type="button" role="tab" aria-selected={view === option.id}
              onClick={() => { setView(option.id); setPage(1); setSelectedBooking(null); setLoading(true); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold ${view === option.id ? 'bg-[#6E1B34] text-white' : 'bg-gray-100 text-gray-700'}`}>
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            <Input
              placeholder="Search by devotee, puja, temple, package, or booking ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-xs h-10"
            />
          </div>
        </div>

        {/* Status Filter Tabs */}
        {view === 'bookings' && <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
          {[
            { id: 'all', label: 'All' },
            { id: 'pending', label: 'Pending' },
            { id: 'confirmed', label: 'Confirmed' },
            { id: 'processing', label: 'Processing' },
            { id: 'puja_scheduled', label: 'Puja Scheduled' },
            { id: 'puja_completed', label: 'Puja Completed' },
            { id: 'cancelled', label: 'Cancelled' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setStatusFilter(tab.id); setPage(1); }}
              className={`px-3 py-1 rounded-xl font-semibold transition-all whitespace-nowrap ${
                statusFilter === tab.id
                  ? 'bg-[#6E1B34] text-white shadow-2xs'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>}
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#FBF4E7] text-[#4E1225] font-bold uppercase tracking-wider border-b border-[#E7D9BC]">
              <tr>
                <th className="px-4 py-3">Booking ID</th>
                <th className="px-4 py-3">Puja &amp; Package</th>
                <th className="px-4 py-3">Devotees &amp; Gotra</th>
                <th className="px-4 py-3">Puja Date</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Prasad</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">Loading sacred bookings...</td>
                </tr>
              ) : filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">No bookings match the filter criteria</td>
                </tr>
              ) : (
                filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 font-mono font-bold text-gray-700">
                      #{booking.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-bold text-[#2A1F16]">{booking.puja?.name || 'Puja Ceremony'}</p>
                      <p className="text-[11px] text-gray-500">
                        {booking.package_name || 'Standard Package'} &bull; {booking.temple?.name || 'Sanctum'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-800">
                        {booking.devotee_names?.slice(0, 2).join(', ') || booking.user?.name || 'Devotee'}
                        {(booking.devotee_names?.length || 0) > 2 && ` +${booking.devotee_names.length - 2} more`}
                      </p>
                      {booking.gotras?.[0] && (
                        <span className="text-[10px] text-gray-500">Gotra: {booking.gotras[0]}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {booking.puja_date
                        ? new Date(booking.puja_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                        : 'Anytime'}
                    </td>
                    <td className="px-4 py-3 font-price font-bold text-[#6E1B34]">
                      ₹{booking.amount?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      {booking.has_prasad ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <Truck className="w-2.5 h-2.5" /> Yes
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {booking.is_draft ? (
                        <span className="inline-flex px-2 py-1 rounded-lg bg-amber-100 text-amber-800 font-bold">
                          {booking.status === 'expired' ? 'Expired draft' : 'Awaiting payment'}
                        </span>
                      ) : <select
                        value={booking.status}
                        onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                        className={`text-xs font-bold px-2 py-1 rounded-lg border border-transparent focus:border-gray-300 ${statusColors[booking.status] || 'bg-gray-100 text-gray-700'}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="processing">Processing</option>
                        <option value="puja_scheduled">Puja Scheduled</option>
                        <option value="puja_completed">Puja Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedBooking(booking)}
                        className="text-[#6E1B34] hover:bg-[#FBF4E7] p-1.5 rounded-lg"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AdminPagination page={page} pages={pages} total={total} onPageChange={setPage} />

      {/* ═══ BOOKING DETAILS MODAL ═══ */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#E7D9BC] shadow-2xl p-6 space-y-5"
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#B85A15] bg-orange-50 px-2.5 py-0.5 rounded-md">
                  {selectedBooking.is_draft ? 'Draft Booking Details' : 'Puja Booking Details'}
                </span>
                <h3 className="font-heading text-lg font-bold text-[#4E1225] mt-1">
                  #{selectedBooking.id}
                </h3>
              </div>
              <button
                onClick={() => setSelectedBooking(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center font-bold"
              >
                ✕
              </button>
            </div>

            {selectedBooking.is_draft && (
              <p className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                {selectedBooking.status === 'expired' ? 'This draft has expired.' : 'Awaiting payment.'} This draft is not a confirmed booking. Expiry: {new Date(selectedBooking.expires_at).toLocaleString('en-IN')}.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              {/* Puja & Package Details */}
              <div className="bg-[#FBF4E7] p-4 rounded-2xl border border-[#E7D9BC] space-y-2">
                <h4 className="font-bold text-[#4E1225] uppercase tracking-wider text-[11px]">Ritual Details</h4>
                <p><strong>Puja:</strong> {selectedBooking.puja?.name || selectedBooking.puja_id}</p>
                <p><strong>Package:</strong> {selectedBooking.package_name || 'Standard'} (₹{selectedBooking.package_price || selectedBooking.amount})</p>
                <p><strong>Temple:</strong> {selectedBooking.temple?.name || 'Sanctum'}</p>
                <p><strong>Puja Date:</strong> {selectedBooking.puja_date || 'Anytime'}</p>
                <p><strong>Total Amount:</strong> <span className="font-price font-bold text-[#6E1B34]">₹{selectedBooking.amount?.toLocaleString()}</span></p>
                <p><strong>Payment Method:</strong> {selectedBooking.payment_method || 'Online'}</p>
              </div>

              {/* Customer & Devotees */}
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 space-y-2">
                <h4 className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">Customer &amp; Devotees</h4>
                <p><strong>Booked By:</strong> {selectedBooking.user?.name || selectedBooking.contact?.name || selectedBooking.delivery_address?.name || 'Devotee'}</p>
                <p><strong>Email:</strong> {selectedBooking.user?.email || selectedBooking.contact?.email || 'N/A'}</p>
                <p><strong>Phone No.:</strong> {selectedBooking.contact?.whatsapp || selectedBooking.delivery_address?.whatsapp || selectedBooking.user?.phone || 'N/A'}</p>
                {selectedBooking.delivery_address?.whatsapp && (
                  <p><strong>WhatsApp:</strong> {selectedBooking.delivery_address.whatsapp}</p>
                )}
                <div className="pt-1 border-t border-gray-200">
                  <strong>Devotees Chanted in Sankalp:</strong>
                  <ul className="list-disc pl-4 mt-1 space-y-0.5">
                    {selectedBooking.devotee_names?.map((name, i) => (
                      <li key={i}>
                        {name} {selectedBooking.gotras?.[i] && `(Gotra: ${selectedBooking.gotras[i]})`}
                      </li>
                    )) || <li>None specified</li>}
                  </ul>
                </div>
              </div>
            </div>

            {/* Puja Wish / Sankalp */}
            {selectedBooking.puja_wish && (
              <div className="p-3.5 bg-orange-50/60 rounded-2xl border border-orange-200 text-xs">
                <strong className="text-[#B85A15] block mb-1">Devotee Puja Wish / Sankalp:</strong>
                <p className="italic text-gray-700 bg-white p-2.5 rounded-xl border border-orange-100">
                  "{selectedBooking.puja_wish}"
                </p>
              </div>
            )}

            {/* Prasad Delivery Address */}
            {selectedBooking.has_prasad && (
              <div className="p-3.5 bg-emerald-50/60 rounded-2xl border border-emerald-200 text-xs space-y-1">
                <div className="flex items-center gap-1.5 text-emerald-800 font-bold">
                  <Truck className="w-4 h-4" />
                  <span>Consecrated Prasad Courier Address</span>
                </div>
                <p className="text-gray-700">
                  {selectedBooking.delivery_address?.name} &bull; {selectedBooking.delivery_address?.phone}<br />
                  {selectedBooking.delivery_address?.address}, {selectedBooking.delivery_address?.address_line2 ? `${selectedBooking.delivery_address.address_line2}, ` : ''}
                  {selectedBooking.delivery_address?.city}, {selectedBooking.delivery_address?.state} - {selectedBooking.delivery_address?.pincode}
                </p>
              </div>
            )}

            {/* Add-ons & Bhet Items */}
            {(selectedBooking.bhet_items?.length > 0 || selectedBooking.addons?.length > 0 || selectedBooking.dakshina > 0 || selectedBooking.store_products?.length > 0) && (
              <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 text-xs space-y-2">
                <strong className="text-gray-700 block">Offerings &amp; Add-ons:</strong>
                <div className="grid grid-cols-2 gap-2 text-gray-600">
                  {selectedBooking.dakshina > 0 && (
                    <p>• Pandit Dakshina: ₹{selectedBooking.dakshina}</p>
                  )}
                  {selectedBooking.bhet_items?.map((b, i) => (
                    <p key={i}>• {b.name} (x{b.quantity}): ₹{b.price * b.quantity}</p>
                  ))}
                  {selectedBooking.addons?.map((a, i) => (
                    <p key={i}>• {a.name} (x{a.quantity}): ₹{a.price * a.quantity}</p>
                  ))}
                  {selectedBooking.store_products?.map((p, i) => (
                    <p key={i}>• Consecrated Item: {p.name} (x{p.quantity})</p>
                  ))}
                </div>
              </div>
            )}

            {/* Status & Tracking Management */}
            {!selectedBooking.is_draft && <div className="space-y-3 pt-2 border-t border-gray-100">
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Update Booking Status</label>
                <div className="flex flex-wrap gap-1.5">
                  {['pending', 'confirmed', 'processing', 'puja_scheduled', 'puja_completed', 'cancelled'].map(st => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => updateBookingStatus(selectedBooking.id, st)}
                      className={`px-3 py-1 rounded-xl text-xs font-semibold capitalize transition-all border ${
                        selectedBooking.status === st
                          ? 'bg-[#6E1B34] text-white border-[#6E1B34]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {st.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prasad Tracking Updates */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Update Prasad Courier Tracking</label>
                <div className="flex flex-wrap gap-1.5">
                  {['puja_scheduled', 'puja_completed', 'offering_prepared', 'courier_picked', 'in_transit', 'delivered'].map(trackSt => (
                    <button
                      key={trackSt}
                      type="button"
                      onClick={() => updateTrackingStatus(selectedBooking.id, trackSt)}
                      className="px-2.5 py-1 rounded-xl text-[11px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 capitalize border border-gray-200"
                    >
                      {trackSt.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Video URL */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Sankalp Proof Video URL (WhatsApp / YouTube)</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://youtu.be/... or video link"
                    defaultValue={selectedBooking.video_url || ''}
                    id="video-url-input"
                    className="text-xs h-9"
                  />
                  <Button
                    size="sm"
                    className="btn-saffron text-xs h-9 px-4"
                    onClick={() => {
                      const val = document.getElementById('video-url-input').value;
                      if (val) updateVideoUrl(selectedBooking.id, val);
                      else toast.error('Please enter a valid video link');
                    }}
                  >
                    Save Video
                  </Button>
                </div>
              </div>
            </div>}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setSelectedBooking(null)} className="text-xs">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
